import { FieldArray, FormikState } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TariffRule as TariffRuleType } from 'subdomains/cpo/types/charging-policy';
import { Button } from 'semantic';
import { FormController } from '../../../../components/FormController';
import { TariffRuleFormFields } from './TariffRuleFormFields';
import { TariffRulePreview } from './TariffRulePreview';
import { tariffRuleSchema } from '../../validation';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  createArrayFormItemWithId,
  useEnsureArrayFormItemIds,
} from 'utils/arrayformid';

type Props = {
  values?: FormikState<TariffRuleType[]>['values'];
};

export const TariffRules = (props: Props) => {
  const { t } = useTranslation();

  const tariffRulesWithTempIDs = useEnsureArrayFormItemIds(props.values);

  return (
    <>
      <FieldArray name="tariffRules">
        {({ remove, replace, move, unshift }) => {
          const onDragEnd = (result: DropResult) => {
            if (!result.destination) return;
            if (result.source.index !== result.destination.index) {
              move(result.source.index, result.destination.index);
            }
          };

          return (
            <>
              <Button
                fluid
                basic
                icon="plus"
                style={{
                  border: '1px solid #8c8c8c',
                  marginLeft: '0',
                  height: '4rem',
                  marginBottom: '1rem',
                }}
                content={t(
                  'chargingPolicy.pricing.addTariffRule',
                  'New tariff'
                )}
                onClick={() =>
                  unshift(createArrayFormItemWithId({ __initial: true }))
                }
              />
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="tariff-rules-droppable">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {tariffRulesWithTempIDs?.map((tariffRule, index) => (
                        <Draggable
                          key={tariffRule.__id}
                          draggableId={String(tariffRule.__id)}
                          index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                marginBottom: '1rem',
                                ...provided.draggableProps.style,
                              }}>
                              <FormController
                                values={tariffRule}
                                onSubmit={(values) => {
                                  replace(index, {
                                    ...values,
                                    __initial: undefined,
                                  });
                                }}
                                onRemove={() => remove(index)}
                                validationSchema={tariffRuleSchema}
                                formComponent={TariffRuleFormFields}
                                previewComponent={TariffRulePreview}
                                openInEditMode={tariffRule.__initial}
                                removeOnCancel={tariffRule.__initial}
                                dragHandleProps={provided.dragHandleProps ?? {}}
                                currency="EUR"
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {!tariffRulesWithTempIDs?.length && (
                        <p>
                          {t(
                            'chargingPolicy.pricing.noTariffRules',
                            'There are no tariff rules to display.'
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          );
        }}
      </FieldArray>
    </>
  );
};
